/**
 * Deterministic in-memory queue used by clients and tests. A browser adapter
 * can persist the same records in IndexedDB without changing retry semantics.
 */
export class MutationQueue {
    records = new Map();
    enqueue(record) {
        const existing = this.records.get(record.id);
        if (existing)
            return { ...existing };
        const next = { ...record, attempts: 0, status: 'queued', createdAt: record.createdAt ?? new Date().toISOString() };
        this.records.set(next.id, next);
        return { ...next };
    }
    /** Replace a queued edit for the same entity without changing its stable operation id. */
    enqueueLatest(record) {
        const existing = this.records.get(record.id);
        if (existing?.status === 'in_flight')
            return { ...existing };
        if (existing) {
            const next = {
                ...record,
                attempts: 0,
                status: 'queued',
                createdAt: existing.createdAt,
            };
            this.records.set(next.id, next);
            return { ...next };
        }
        return this.enqueue(record);
    }
    ready(limit = 20) {
        const output = [];
        for (const record of this.records.values()) {
            if (record.status !== 'queued' && record.status !== 'failed')
                continue;
            if ((record.dependsOn ?? []).some((id) => this.records.get(id)?.status !== 'applied'))
                continue;
            output.push({ ...record, status: 'in_flight' });
            record.status = 'in_flight';
            if (output.length >= limit)
                break;
        }
        return output;
    }
    markApplied(id) {
        const record = this.records.get(id);
        if (record)
            record.status = 'applied';
    }
    markFailed(id, error) {
        const record = this.records.get(id);
        if (record) {
            record.status = 'failed';
            record.attempts += 1;
            record.lastError = error.slice(0, 500);
        }
    }
    markConflict(id, error = 'VERSION_CONFLICT') {
        const record = this.records.get(id);
        if (record) {
            record.status = 'conflict';
            record.attempts += 1;
            record.lastError = error.slice(0, 500);
        }
    }
    setStatus(id, status, error) {
        const record = this.records.get(id);
        if (!record)
            return;
        record.status = status;
        if (error !== undefined)
            record.lastError = error.slice(0, 500);
    }
    retry(id) {
        const record = this.records.get(id);
        if (record && (record.status === 'failed' || record.status === 'conflict'))
            record.status = 'queued';
    }
    removeApplied() {
        for (const [id, record] of this.records)
            if (record.status === 'applied')
                this.records.delete(id);
    }
    remove(id) {
        this.records.delete(id);
    }
    clearWorkspace(workspaceId) {
        for (const [id, record] of this.records)
            if (record.workspaceId === workspaceId)
                this.records.delete(id);
    }
    rebindWorkspace(fromWorkspaceId, toWorkspaceId) {
        for (const record of this.records.values())
            if (record.workspaceId === fromWorkspaceId)
                record.workspaceId = toWorkspaceId;
    }
    snapshot() {
        return [...this.records.values()].map((record) => ({ ...record, dependsOn: [...(record.dependsOn ?? [])] }));
    }
    restore(record) {
        if (!this.records.has(record.id))
            this.records.set(record.id, { ...record, dependsOn: [...(record.dependsOn ?? [])] });
    }
}
export function isCursorExpired(error) {
    if (!error || typeof error !== 'object')
        return false;
    const value = error;
    return value.status === 410 || value.code === 'CURSOR_EXPIRED' || value.payload?.code === 'CURSOR_EXPIRED';
}
/** SHA-256 of the exact UTF-8 JSON representation sent in the mutation body. */
export async function sha256Hex(value) {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle)
        throw new Error('Web Crypto is unavailable');
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(value));
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
export async function createSyncMutation(input) {
    const serialized = JSON.stringify(input.payload);
    if (serialized === undefined)
        throw new Error('Mutation payload is not serializable');
    return {
        id: input.operationId,
        operation_id: input.operationId,
        type: input.type ?? 'upsert',
        entity: input.entity,
        workspace_id: input.workspaceId,
        entity_id: input.entityId,
        base_version: input.baseVersion,
        request_hash: await sha256Hex(serialized),
        payload: input.payload,
    };
}
/**
 * Shared pull-then-push engine. Clients provide storage and transport, so the
 * cursor/atomic-apply/conflict semantics stay identical across Web and
 * Desktop implementations.
 */
export class SyncEngine {
    remote;
    store;
    running = false;
    constructor(remote, store) {
        this.remote = remote;
        this.store = store;
    }
    async syncAll() {
        if (this.running)
            return { workspaces: 0, applied: 0, conflicts: 0 };
        this.running = true;
        try {
            const status = await this.remote.status();
            for (const workspace of status.workspaces)
                await this.syncWorkspace(workspace.workspace_id);
            const result = await this.drainOutbox();
            return { workspaces: status.workspaces.length, ...result };
        }
        finally {
            this.running = false;
        }
    }
    async syncWorkspace(workspaceId) {
        let cursor = await this.store.getCursor(workspaceId);
        if (cursor === undefined) {
            const snapshot = await this.remote.bootstrap(workspaceId);
            await this.store.replaceWorkspace(workspaceId, snapshot.items, snapshot.cursor, snapshot.snapshot_token);
            return;
        }
        try {
            while (true) {
                const delta = await this.remote.delta(workspaceId, cursor, 100);
                for (const event of delta.events) {
                    // The account cursor also carries team membership, proposals and
                    // release notifications. Their authoritative screens use their own
                    // endpoints; only playbook entity events belong in this local row
                    // store. Still advance the cursor so an unsupported event cannot
                    // wedge every later sync attempt.
                    if (event.entity !== 'playbook')
                        continue;
                    if (event.type === 'delete')
                        await this.store.remove(event.entity, event.entity_id);
                    else
                        await this.store.upsert(event.entity, await this.remote.fetch(event.entity, event.entity_id));
                }
                cursor = delta.next_cursor;
                await this.store.putCursor(workspaceId, cursor);
                if (!delta.has_more)
                    return;
            }
        }
        catch (error) {
            if (!isCursorExpired(error))
                throw error;
            const snapshot = await this.remote.bootstrap(workspaceId);
            await this.store.replaceWorkspace(workspaceId, snapshot.items, snapshot.cursor, snapshot.snapshot_token);
        }
    }
    async drainOutbox() {
        const records = (await this.store.listOutbox()).filter((record) => record.status === 'queued' || record.status === 'failed' || record.status === 'in_flight');
        let applied = 0;
        let conflicts = 0;
        for (let start = 0; start < records.length; start += 100) {
            const batch = records.slice(start, start + 100);
            const operations = [];
            for (const record of batch) {
                if (!record.operationId || !record.entityId || record.baseVersion === undefined || !record.requestHash) {
                    await this.store.markOutbox(record.id, 'failed', 'MUTATION_METADATA_MISSING');
                    continue;
                }
                operations.push({
                    id: record.id,
                    operation_id: record.operationId,
                    type: 'upsert',
                    entity: record.entity,
                    workspace_id: record.workspaceId,
                    entity_id: record.entityId,
                    base_version: record.baseVersion,
                    request_hash: record.requestHash,
                    payload: record.payload,
                });
            }
            if (operations.length === 0)
                continue;
            const key = `sync-${await sha256Hex(JSON.stringify(operations.map((operation) => ({ operation_id: operation.operation_id, request_hash: operation.request_hash }))))}`;
            let response;
            try {
                response = await this.remote.sendMutations(operations, key);
            }
            catch (error) {
                for (const operation of operations)
                    await this.store.markOutbox(operation.id, 'failed', error instanceof Error ? error.message : 'SYNC_FAILED');
                continue;
            }
            for (const operation of operations) {
                const result = response.results.find((candidate) => candidate.id === operation.operation_id || candidate.id === operation.id);
                if (!result) {
                    await this.store.markOutbox(operation.id, 'failed', 'MUTATION_RESULT_MISSING');
                }
                else if (result.status === 'applied') {
                    const entityId = result?.entity_id ?? operation.entity_id;
                    try {
                        await this.store.upsert(operation.entity, await this.remote.fetch(operation.entity, entityId));
                    }
                    catch { /* the delta will recover a missing response */ }
                    await this.store.removeOutbox(operation.id);
                    applied += 1;
                }
                else if (result.code === 'VERSION_CONFLICT') {
                    await this.store.markOutbox(operation.id, 'conflict', result.code);
                    const record = batch.find((candidate) => candidate.id === operation.id);
                    if (record)
                        await this.store.preserveConflict(record, result.current);
                    conflicts += 1;
                }
                else {
                    await this.store.markOutbox(operation.id, 'failed', result.code ?? 'MUTATION_REJECTED');
                }
            }
        }
        return { applied, conflicts };
    }
}
/** Minimal IndexedDB adapter for account-scoped records. */
export class PersonalIndexedDB {
    options;
    constructor(options = {}) {
        this.options = { databaseName: options.databaseName ?? 'prompt-assistant', storeName: options.storeName ?? 'personal', version: options.version ?? 1, indexedDBFactory: options.indexedDBFactory };
    }
    async put(key, value) {
        const db = await this.open();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(this.options.storeName, 'readwrite');
            tx.objectStore(this.options.storeName).put({ key, value });
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
        db.close();
    }
    async get(key) {
        const db = await this.open();
        const value = await new Promise((resolve, reject) => {
            const tx = db.transaction(this.options.storeName, 'readonly');
            const request = tx.objectStore(this.options.storeName).get(key);
            request.onsuccess = () => resolve(request.result?.value);
            request.onerror = () => reject(request.error);
        });
        db.close();
        return value;
    }
    async delete(key) {
        const db = await this.open();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(this.options.storeName, 'readwrite');
            tx.objectStore(this.options.storeName).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
        db.close();
    }
    async clear() {
        const db = await this.open();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(this.options.storeName, 'readwrite');
            tx.objectStore(this.options.storeName).clear();
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
        db.close();
    }
    async entries() {
        const db = await this.open();
        const values = await new Promise((resolve, reject) => {
            const tx = db.transaction(this.options.storeName, 'readonly');
            const request = tx.objectStore(this.options.storeName).getAll();
            request.onsuccess = () => resolve((request.result ?? []));
            request.onerror = () => reject(request.error);
        });
        db.close();
        return values;
    }
    open() {
        const factory = this.options.indexedDBFactory ?? globalThis.indexedDB;
        if (!factory)
            return Promise.reject(new Error('IndexedDB unavailable'));
        return new Promise((resolve, reject) => {
            const request = factory.open(this.options.databaseName, this.options.version);
            request.onupgradeneeded = () => {
                if (!request.result.objectStoreNames.contains(this.options.storeName))
                    request.result.createObjectStore(this.options.storeName, { keyPath: 'key' });
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }
}
